import React, { useCallback, useContext, useMemo, useState } from 'react';
import styled from 'styled-components';
import _ from 'lodash';

import ShowOnMouseOverCss from 'cssSnippets/ShowOnMouseOverCss';
import Row from 'components/Common/Row';
import UsersContext from 'contexts/UsersContext';
import AccountPickerContext from 'contexts/AccountPickerContext';
import { GroupsContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/GroupsProvider';
import usePopup from 'hooks/usePopup';
import isDefined from 'isDefined';
import Button from 'kingpin/atoms/Button';
import Icon from 'kingpin/atoms/Icon';
import TextInput from 'kingpin/atoms/TextInput';
import Typography from 'kingpin/atoms/Typography';

import STORE from 'store';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import appTypeCheckers from '../../../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider/appTypeCheckers';
import { ShareContentPopup } from '../../../Content/V3ContentShareButton';
import Alert from '../../../../../kingpin/Alert';
import V3GroupNameWithMembershipPopup from '../../../Content/V3GroupNameWithMembershipPopup';

export const HeaderDiv = styled.div`
  height: 48px;
  background: var(--color-bg-tertiary);
  border-radius: 8px;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export const IconDiv = styled.div`
  width: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const ListRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  height: 48px;
  border-bottom: 1px solid var(--color-border-secondary);
  overflow: hidden;
  ${ShowOnMouseOverCss}
`;

const GroupRow = ({
  group,
  app,
}: {
  group: UserManagement.Group;
  app: V3.App;
}) => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const onRemoveClicked = useCallback(() => {
    if (isSaving) {
      return;
    }

    setIsSaving(true);
    const newApp: V3.App = {
      ...app,
      appViewersAccess: {
        ...app.appViewersAccess,
        groupIds: app.appViewersAccess.groupIds.filter(
          (groupId) => groupId !== group.id,
        ),
      },
    };

    if (appTypeCheckers.isWorkspace(newApp)) {
      STORE.contentDistributions
        .getWorkspacesRef({
          accountId: selectedAccountId,
        })
        .doc(newApp.id)
        .set(newApp)
        .then(() => {
          setIsSaving(false);
        });
    } else if (appTypeCheckers.isBonusApp(newApp)) {
      STORE.contentDistributions
        .getBonusAppsRef({
          accountId: selectedAccountId,
        })
        .doc(newApp.id)
        .set(newApp)
        .then(() => {
          setIsSaving(false);
        });
    } else if (appTypeCheckers.isLegacyBonusApp(newApp)) {
      STORE.contentDistributions
        .getLegacyBonusAppsRef({
          accountId: selectedAccountId,
        })
        .doc(newApp.id)
        .set(newApp)
        .then(() => {
          setIsSaving(false);
        });
    } else if (appTypeCheckers.isGeneralApp(newApp)) {
      STORE.contentDistributions
        .getGeneralAppsRef({
          accountId: selectedAccountId,
        })
        .doc(newApp.id)
        .set(newApp)
        .then(() => {
          setIsSaving(false);
        });
    }
  }, [group.id, isSaving, selectedAccountId, app]);

  return (
    <ListRow>
      <Row>
        <IconDiv>
          <Icon icon={'group'} width={16} height={16} />
        </IconDiv>
        <V3GroupNameWithMembershipPopup group={group} />
      </Row>
      <div className="showOnMouseOver">
        <Button
          size={'Medium'}
          type={'Secondary'}
          label={'Remove'}
          onClick={onRemoveClicked}
        />
      </div>
    </ListRow>
  );
};

const UserRow = ({
  user,
  app,
}: {
  user: UserManagement.SignedUpUser | UserManagement.PendingUser;
  app: V3.App;
}) => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const onRemoveClicked = useCallback(() => {
    if (isSaving) {
      return;
    }

    setIsSaving(true);
    const newApp: V3.App = {
      ...app,
      appViewersAccess: {
        ...app.appViewersAccess,
        userIds: app.appViewersAccess.userIds.filter(
          (userId) => userId !== user.id,
        ),
      },
    };
    if (appTypeCheckers.isWorkspace(newApp)) {
      STORE.contentDistributions
        .getWorkspacesRef({
          accountId: selectedAccountId,
        })
        .doc(newApp.id)
        .set(newApp)
        .then(() => {
          setIsSaving(false);
        });
    } else if (appTypeCheckers.isBonusApp(newApp)) {
      STORE.contentDistributions
        .getBonusAppsRef({
          accountId: selectedAccountId,
        })
        .doc(newApp.id)
        .set(newApp)
        .then(() => {
          setIsSaving(false);
        });
    } else if (appTypeCheckers.isLegacyBonusApp(newApp)) {
      STORE.contentDistributions
        .getLegacyBonusAppsRef({
          accountId: selectedAccountId,
        })
        .doc(newApp.id)
        .set(newApp)
        .then(() => {
          setIsSaving(false);
        });
    } else if (appTypeCheckers.isGeneralApp(newApp)) {
      STORE.contentDistributions
        .getGeneralAppsRef({
          accountId: selectedAccountId,
        })
        .doc(newApp.id)
        .set(newApp)
        .then(() => {
          setIsSaving(false);
        });
    }
  }, [user.id, isSaving, selectedAccountId, app]);

  return (
    <ListRow>
      <Row>
        <IconDiv />
        <Typography.Body type={'Body 14'}>{user.displayName}</Typography.Body>
      </Row>
      <div className="showOnMouseOver">
        <Button
          size={'Medium'}
          type={'Secondary'}
          label={'Remove'}
          onClick={onRemoveClicked}
        />
      </div>
    </ListRow>
  );
};

export const ListDiv = styled.div`
  overflow-y: scroll;
`;

const AppViewersList = ({ app }: { app: V3.App }) => {
  const { isOpen, open, close } = usePopup();
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { signedUpAndPendingUsers } = useContext(UsersContext);
  const { groups: allGroups } = useContext(GroupsContext);

  const [searchText, setSearchText] = useState<string>('');
  const onSearchTextChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchText(event.target.value);
    },
    [],
  );

  const groups = useMemo(() => {
    return _.sortBy(
      app.appViewersAccess.groupIds
        .map((gid) => allGroups.find((g) => g.id === gid))
        .filter(isDefined)
        .filter((g) => {
          if (searchText === '') {
            return true;
          }

          return g.name.toLowerCase().includes(searchText.toLowerCase());
        }),
      'name',
    );
  }, [allGroups, searchText, app.appViewersAccess.groupIds]);

  const users = useMemo(() => {
    return _.sortBy(
      app.appViewersAccess.userIds
        .map((uid) => signedUpAndPendingUsers.find((u) => u.id === uid))
        .filter(isDefined)
        .filter((u) => {
          if (searchText === '') {
            return true;
          }

          return (u.displayName ?? '')
            .toLowerCase()
            .includes(searchText.toLowerCase());
        }),
      'displayName',
    );
  }, [searchText, app.appViewersAccess.userIds, signedUpAndPendingUsers]);

  const onShareSaved = useCallback(
    (newAccess: V3Access) => {
      const newApp: V3.App = {
        ...app,
        appViewersAccess: newAccess,
      };

      if (appTypeCheckers.isWorkspace(newApp)) {
        return STORE.contentDistributions
          .getWorkspacesRef({
            accountId: selectedAccountId,
          })
          .doc(newApp.id)
          .set(newApp);
      } else if (appTypeCheckers.isBonusApp(newApp)) {
        return STORE.contentDistributions
          .getBonusAppsRef({
            accountId: selectedAccountId,
          })
          .doc(newApp.id)
          .set(newApp);
      } else if (appTypeCheckers.isLegacyBonusApp(newApp)) {
        return STORE.contentDistributions
          .getLegacyBonusAppsRef({
            accountId: selectedAccountId,
          })
          .doc(newApp.id)
          .set(newApp);
      } else if (appTypeCheckers.isGeneralApp(newApp)) {
        return STORE.contentDistributions
          .getGeneralAppsRef({
            accountId: selectedAccountId,
          })
          .doc(newApp.id)
          .set(newApp);
      } else {
        const e = new Error();
        e.name = 'Unknown app type';
        throw e;
      }
    },
    [selectedAccountId, app],
  );

  if (app.appViewersAccess.isPublic) {
    return (
      <div style={{ padding: '24px 0px' }}>
        {/* eslint-disable-next-line react/jsx-no-undef */}
        <Alert
          type={'Info'}
          title={'This asset is public'}
          body={
            'This asset is available to everyone and will appear in their all assets'
          }
        />
      </div>
    );
  }

  return (
    <>
      <Row spaceBetween centerAlign style={{ height: 64 }}>
        <div style={{ width: 300 }}>
          <TextInput
            value={searchText}
            onChange={onSearchTextChanged}
            icon={'search'}
            placeholder={'Search'}
          />
        </div>

        <Button
          size={'Medium'}
          type={'Primary'}
          label={'Add Users / Groups'}
          onClick={open}
        />
      </Row>
      <HeaderDiv>
        <IconDiv />
        <Typography.Body type={'Label'}>Name</Typography.Body>
      </HeaderDiv>
      <ListDiv>
        {groups.map((group) => (
          <GroupRow key={group.id} group={group} app={app} />
        ))}
        {users.map((user) => (
          <UserRow key={user.id} user={user} app={app} />
        ))}
      </ListDiv>
      <ModalTransition>
        {isOpen && (
          <Modal
            onClose={close}
            shouldScrollInViewport={false}
            autoFocus={false}
          >
            <ShareContentPopup
              close={close}
              onSave={onShareSaved}
              initialAccess={app.appViewersAccess}
            />
          </Modal>
        )}
      </ModalTransition>
    </>
  );
};

export default AppViewersList;
